import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Checkbox,
} from "@mui/material";
import Rating from "@mui/material/Rating";
import { connect, useDispatch } from "react-redux";
import styles from "./reviews.module.scss";
import StatusCard from "../../components/uiComponents/statusCard/StatusCard";
import { GenCustomTable } from "../../components/customComponents/genCustomTable/GenCustomTable";
import {
  getAllCustomer,
  getAllProduct,
  getAllReview,
  updateReview as updateReviewAction,
} from "../../store/actions";
import { getObjectFromArrayOfObjects } from "../../utils/common/CommonFunctions";

const STATUS_MAP = {
  1: "Approved",
  0: "Pending",
  [-1]: "Disapproved",
};

const STATUS_COLOR = {
  1: "success",
  0: "warning",
  [-1]: "error",
};

const STATUS_OPTIONS = [
  { value: 0, label: STATUS_MAP[0] },
  { value: 1, label: STATUS_MAP[1] },
  { value: -1, label: STATUS_MAP[-1] },
];

const DEFAULT_STATUS_FILTER = [];

const clampRating = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 0;
  return Math.min(5, Math.max(0, numeric));
};

const normalizeReview = (review = {}) => ({
  ...review,
  rating: clampRating(review?.rating ?? 0),
  status: Number.isFinite(Number(review?.status)) ? Number(review.status) : 0,
});

const tableColumns = [
  { id: "col-sNo", label: "S No", minWidth: 80, align: "center" },
  // { id: "col-id", label: "ID", minWidth: 80, align: "center" },
  { id: "col-title", label: "Title", minWidth: 200, align: "center" },
  { id: "col-customer", label: "Customer", minWidth: 200, align: "center" },
  { id: "col-product", label: "Product", minWidth: 200, align: "center" },
  { id: "col-rating", label: "Rating", minWidth: 140, align: "center" },
  { id: "col-status", label: "Status", minWidth: 160, align: "center" },
  // { id: "col-active", label: "Active", minWidth: 140, align: "center" },
  { id: "col-actions", label: "Actions", minWidth: 220, align: "center" },
];

const Reviews = ({ reviews: reviewsFromStore = [], products, customers }) => {
  const dispatch = useDispatch();
  const [localReviews, setLocalReviews] = useState([]);
  const [initialising, setInitialising] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [updatingIds, setUpdatingIds] = useState([]);
  const [hasRequested, setHasRequested] = useState(false);

  useEffect(() => {
    if (!hasRequested) {
      dispatch(getAllReview());

      setHasRequested(true);
    }
  }, [dispatch, hasRequested]);

  useEffect(() => {
    if (!Array.isArray(reviewsFromStore)) {
      setLocalReviews([]);
      setInitialising(false);
      return;
    }

    setLocalReviews(reviewsFromStore.map(normalizeReview));
    setInitialising(false);
  }, [reviewsFromStore]);

  useEffect(() => {
    if (!selectedReview) return;
    const refreshed = localReviews.find(
      (review) => review.id === selectedReview.id
    );
    if (refreshed && refreshed !== selectedReview) {
      setSelectedReview(refreshed);
      setPendingStatus(refreshed.status);
    }
  }, [localReviews, selectedReview]);

  const filtersToApply = useMemo(() => {
    if (statusFilter.length === 0)
      return STATUS_OPTIONS.map((option) => option.value);
    return statusFilter;
  }, [statusFilter]);

  const filteredReviews = useMemo(
    () =>
      localReviews.filter((review) =>
        filtersToApply.includes(Number(review.status))
      ),
    [localReviews, filtersToApply]
  );

  const isUpdating = useCallback(
    (id) => updatingIds.includes(id),
    [updatingIds]
  );

  const updateLocalReview = useCallback((id, changes) => {
    setLocalReviews((prev) =>
      prev.map((review) =>
        review.id === id ? normalizeReview({ ...review, ...changes }) : review
      )
    );
  }, []);

  const dispatchUpdate = useCallback(
    (review, nextStatus) =>
      new Promise((resolve, reject) => {
        dispatch(
          updateReviewAction({
            payload: { ...review, status: nextStatus },
            callback: {
              success: (data) => resolve(data),
              failure: (err) => reject(err),
            },
          })
        );
      }),
    [dispatch]
  );

  const performStatusUpdate = useCallback(
    async (id, nextStatus) => {
      const target = localReviews.find((review) => review.id === id);
      if (!target) return false;

      const previousStatus = target.status;
      if (previousStatus === nextStatus) return true;

      setError(null);
      setUpdatingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      updateLocalReview(id, { status: nextStatus });

      try {
        const updated = await dispatchUpdate(target, nextStatus);
        if (updated && typeof updated === "object" && "id" in updated) {
          updateLocalReview(id, updated);
        }
        dispatch(getAllReview());
        return true;
      } catch (err) {
        updateLocalReview(id, { status: previousStatus });
        const message =
          err?.data?.message || err?.message || "Failed to update status.";
        setError(message);
        toast.error(message);
        return false;
      } finally {
        setUpdatingIds((prev) => prev.filter((value) => value !== id));
      }
    },
    [localReviews, updateLocalReview, dispatchUpdate, dispatch]
  );

  const handleFilterChange = (event) => {
    const value = event.target.value;
    const parsed = Array.isArray(value)
      ? value.map((item) => Number(item))
      : [];
    setStatusFilter(parsed.length ? parsed : DEFAULT_STATUS_FILTER);
  };

  const handleRemoveFilter = (status) => {
    setStatusFilter((prev) => {
      const next = prev.filter((value) => value !== status);
      return next.length ? next : DEFAULT_STATUS_FILTER;
    });
  };

  const handleOpenModal = useCallback((review) => {
    setSelectedReview(review);
    setPendingStatus(review.status);
    setModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedReview(null);
    setPendingStatus(null);
  };

  const handleModalSave = async () => {
    if (!selectedReview) return;
    const nextStatus = Number(pendingStatus);
    const success = await performStatusUpdate(selectedReview.id, nextStatus);
    if (success) {
      setSelectedReview((prev) =>
        prev ? { ...prev, status: nextStatus } : prev
      );
      handleCloseModal();
    } else {
      setPendingStatus(selectedReview.status);
    }
  };

  const handleInlineStatusChange = useCallback(
    async (id, value) => {
      await performStatusUpdate(id, Number(value));
    },
    [performStatusUpdate]
  );

  const tableRows = useMemo(
    () =>
      filteredReviews.map((review, idx) => ({
        id: review.id,
        rColumns: [
          idx + 1,
          // review.id,
          <Typography
            key={`title-${review.id}`}
            variant="body2"
            sx={{ maxWidth: 220 }}
          >
            {review?.tittle || "(No title)"}
          </Typography>,
          <Typography
            key={`customer-${review.id}`}
            variant="body2"
            sx={{ maxWidth: 220 }}
          >
            {
              getObjectFromArrayOfObjects(customers, "id", review?.customerId)
                ?.fullName
            }
          </Typography>,
          <Typography
            key={`customer-${review.id}`}
            variant="body2"
            sx={{ maxWidth: 220 }}
          >
            {
              getObjectFromArrayOfObjects(products, "id", review?.productId)
                ?.name
            }
          </Typography>,
          <Rating
            key={`rating-${review.id}`}
            value={review.rating}
            max={5}
            readOnly
            size="small"
          />,
          <Chip
            key={`status-${review.id}`}
            label={STATUS_MAP[review.status] ?? "Unknown"}
            color={STATUS_COLOR[review.status] ?? "default"}
            variant="outlined"
          />,
          // <Chip
          //   key={`active-${review.id}`}
          //   label={review.active ? "Active" : "Inactive"}
          //   color={review.active ? "success" : "default"}
          //   variant={review.active ? "outlined" : "filled"}
          // />,
          <Stack
            key={`actions-${review.id}`}
            direction="column"
            spacing={1}
            alignItems="center"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOpenModal(review)}
              aria-label={`View review ${review.id}`}
            >
              View
            </Button>
          </Stack>,
        ],
      })),
    [filteredReviews, handleInlineStatusChange, handleOpenModal, isUpdating]
  );

  useEffect(() => {
    dispatch(getAllProduct());
    dispatch(getAllCustomer());
  }, []);

  const isLoading = initialising && localReviews.length === 0;

  return (
    <div className={styles.container}>
      <StatusCard leftContent="REVIEW" />
      <Box sx={{ mt: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6"></Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="status-filter-label">Status filter</InputLabel>
            <Select
              labelId="status-filter-label"
              multiple
              value={statusFilter}
              onChange={handleFilterChange}
              label="Status filter"
              renderValue={(selected) => (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={STATUS_MAP[value]}
                      onMouseDown={(event) => event.stopPropagation()}
                      onDelete={() => handleRemoveFilter(value)}
                      size="small"
                    />
                  ))}
                </Stack>
              )}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={statusFilter.includes(option.value)} />
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <GenCustomTable columns={tableColumns} rows={tableRows} />
        )}
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="review-modal-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="review-modal-title">Review Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Title
              </Typography>
              <Typography variant="body1">
                {selectedReview?.tittle ?? "(No title)"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">
                {selectedReview?.description ?? "(No description)"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Rating
              </Typography>
              <Rating value={selectedReview?.rating ?? 0} readOnly max={5} />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={STATUS_MAP[selectedReview?.status] ?? "Unknown"}
                color={STATUS_COLOR[selectedReview?.status] ?? "default"}
                variant="outlined"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="modal-status-select">Status</InputLabel>
            <Select
              labelId="modal-status-select"
              label="Status"
              value={pendingStatus ?? selectedReview?.status ?? 0}
              onChange={(event) => setPendingStatus(Number(event.target.value))}
              fullWidth
              disabled={selectedReview ? isUpdating(selectedReview.id) : false}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleModalSave}
              disabled={
                !selectedReview ||
                pendingStatus === null ||
                Number(pendingStatus) === selectedReview.status ||
                isUpdating(selectedReview.id)
              }
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  reviews: state.review.reviews,
  products: state?.product?.products,
  customers: state?.customer?.customers,
});

export default connect(mapStateToProps, null)(Reviews);
